import axios from 'axios';
import { API_URL, CONFIG } from '@/config';
import { localStateStore } from '@/utils/localStateStore';

export interface DeviceInfo {
  id: string;
  token: string;
  name: string;
  status: 'online' | 'offline';
}

// Karışıklığa neden olabilecek karakterleri çıkardık:
// Çıkarılanlar: 0, O, 1, I, l
const ALLOWED_CHARS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const TOKEN_BACKUP_KEYS = ['veeqDeviceTokenBackup', 'device_token'];
const TOKEN_COOKIE_KEY = 'veeqDeviceToken';

function generateToken(): string {
  // Web Crypto API kullanarak rastgele sayılar oluştur
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  
  // Array'i sayıya çevir
  let num = 0n;
  for (let i = 0; i < array.length; i++) {
    num = num * 256n + BigInt(array[i]);
  }
  
  // Sayıyı 6 karakterli token'a çevir
  let token = '';
  for (let i = 0; i < 6; i++) {
    const index = Number(num % BigInt(ALLOWED_CHARS.length));
    token = ALLOWED_CHARS[index] + token;
    num = num / BigInt(ALLOWED_CHARS.length);
  }
  
  return token;
}

function isValidToken(token: string | null): token is string {
  return Boolean(token && /^[A-Z0-9]{4,16}$/.test(token));
}

function writeTokenCookie(token: string): void {
  if (typeof document === 'undefined') return;

  try {
    const maxAge = 60 * 60 * 24 * 365 * 5;
    document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(token)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
  } catch {
    // ignore cookie errors
  }
}

function readTokenCookie(): string | null {
  if (typeof document === 'undefined') return null;

  try {
    const entry = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(`${TOKEN_COOKIE_KEY}=`));

    return entry ? decodeURIComponent(entry.split('=').slice(1).join('=')) : null;
  } catch {
    return null;
  }
}

function clearTokenCookie(): void {
  if (typeof document === 'undefined') return;

  try {
    document.cookie = `${TOKEN_COOKIE_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch {
    // ignore cookie errors
  }
}

async function getDeviceInfo() {
  console.log('Getting device info...');

  // Browser bilgileri
  const userAgent = navigator.userAgent;
  const browserInfo = userAgent.match(/(chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  const browserName = browserInfo[1] || "Unknown Browser";
  const browserVersion = browserInfo[2] || "Unknown Version";

  // Donanım bilgileri
  const memory = ('deviceMemory' in navigator) ? (navigator as any).deviceMemory : null;
  const cores = ('hardwareConcurrency' in navigator) ? (navigator as any).hardwareConcurrency : null;

  // Ağ bilgileri
  const connection = ('connection' in navigator) ? (navigator as any).connection : ('mozConnection' in navigator) ? (navigator as any).mozConnection : ('webkitConnection' in navigator) ? (navigator as any).webkitConnection : null;
  const networkInfo = connection ? {
    type: connection.type,
    effectiveType: connection.effectiveType,
    downlinkMax: connection.downlinkMax,
    rtt: connection.rtt,
    saveData: connection.saveData,
    onLine: ('onLine' in navigator) && navigator.onLine
  } : null;

  // Batarya bilgileri
  let batteryInfo = null;
  try {
    if ('getBattery' in navigator) {
      const battery: any = await (navigator as any).getBattery();
      batteryInfo = {
        charging: battery.charging,
        level: battery.level,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime
      };
    }
  } catch (error) {
    console.log('Battery API not supported');
  }

  // Ekran bilgileri
  const screenInfo = {
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth,
    orientation: ('orientation' in screen) ? screen.orientation?.type : 'unknown',
    pixelRatio: window.devicePixelRatio
  };

  // Medya özellikleri
  const mediaCapabilities = {
    speakers: ('speechSynthesis' in window),
    microphone: ('mediaDevices' in navigator),
    bluetooth: ('bluetooth' in navigator),
    webrtc: ('RTCPeerConnection' in window),
    midi: ('requestMIDIAccess' in navigator)
  };

  // GPU bilgileri
  let gpuInfo = null;
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      gpuInfo = {
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null,
        webglVersion: gl.getParameter(gl.VERSION),
        shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE)
      };
    }
  } catch (error) {
    console.log('WebGL not supported');
  }

  // Platform ve dil bilgileri
  const platformInfo = {
    platform: ('platform' in navigator) ? navigator.platform : null,
    userAgent: userAgent,
    language: ('language' in navigator) ? navigator.language : null,
    languages: ('languages' in navigator) ? navigator.languages : null,
    cookiesEnabled: ('cookieEnabled' in navigator) && navigator.cookieEnabled,
    doNotTrack: ('doNotTrack' in navigator) && navigator.doNotTrack,
    maxTouchPoints: ('maxTouchPoints' in navigator) && navigator.maxTouchPoints,
    hardwareConcurrency: ('hardwareConcurrency' in navigator) ? navigator.hardwareConcurrency : null,
    deviceMemory: ('deviceMemory' in navigator) ? (navigator as any).deviceMemory : null,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${window.screen.width}x${window.screen.height}`
  };

  const deviceInfo = {
    name: `Web Player (${browserName} ${browserVersion})`,
    type: 'web',
    browserInfo: {
      name: browserName,
      version: browserVersion,
      userAgent: userAgent,
      cookiesEnabled: ('cookieEnabled' in navigator) && navigator.cookieEnabled,
      doNotTrack: ('doNotTrack' in navigator) && navigator.doNotTrack,
      languages: ('languages' in navigator) ? navigator.languages : null,
      onLine: ('onLine' in navigator) && navigator.onLine
    },
    platform: platformInfo.platform, // Sadece platform string'ini gönder
    screen: screenInfo,
    hardware: {
      memory: memory ? `${memory}GB` : 'unknown',
      cores: cores || 'unknown',
      maxTouchPoints: ('maxTouchPoints' in navigator) && navigator.maxTouchPoints,
      gpu: gpuInfo
    },
    network: networkInfo ? `${networkInfo.effectiveType} (RTT: ${networkInfo.rtt}ms)` : 'unknown', // Network bilgisini string olarak gönder
    battery: batteryInfo,
    mediaCapabilities,
    language: platformInfo.language,
    timeZone: platformInfo.timeZone,
    screenResolution: platformInfo.screenResolution,
    userAgent: platformInfo.userAgent,
    timestamp: new Date().toISOString()
  };

  console.log('Final device info:', deviceInfo);
  return deviceInfo;
}

class DeviceService {
  private static instance: DeviceService;
  private deviceInfo: DeviceInfo | null = null;

  private constructor() {}

  static getInstance(): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService();
    }
    return DeviceService.instance;
  }

  async registerDevice(): Promise<string> {
    try {
      console.log('Starting device registration...');

      // Yeni token oluştur
      const token = generateToken();
      console.log('Generated token:', token);
      
      // Device bilgilerini al
      const deviceInfo = await getDeviceInfo();
      console.log('Collected device info:', deviceInfo);

      console.log('Sending registration request to API...');
      const response = await axios.post(`${API_URL}/tokens`, {
        token,
        deviceInfo
      });

      console.log('API Response:', response.data);

      if (response.data && response.data.token) {
        this.deviceInfo = {
          id: response.data.deviceId || '',
          token: response.data.token,
          name: response.data.deviceInfo?.name || 'Web Player',
          status: 'online'
        };
        console.log('Device info updated:', this.deviceInfo);

        const savedToken = response.data.token || token;
        this.persistToken(savedToken);
        console.log('Token saved to localStorage');

        return savedToken;
      }
      
      throw new Error('Device registration failed: Invalid response from server');
    } catch (error: any) {
      console.error('Device registration failed:', error.response?.data || error.message);
      throw error;
    }
  }

  getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }

  private persistToken(token: string): void {
    if (!isValidToken(token)) return;

    localStateStore.setString(CONFIG.token.storageKey, token);
    localStateStore.setString('deviceId', token);
    for (const key of TOKEN_BACKUP_KEYS) {
      localStateStore.setString(key, token);
    }
    writeTokenCookie(token);
  }

  getStoredToken(): string | null {
    const primary = localStateStore.getString(CONFIG.token.storageKey);
    if (isValidToken(primary)) {
      this.persistToken(primary);
      return primary;
    }

    for (const key of TOKEN_BACKUP_KEYS) {
      const backup = localStateStore.getString(key);
      if (isValidToken(backup)) {
        this.persistToken(backup);
        return backup;
      }
    }

    const cookieToken = readTokenCookie();
    if (isValidToken(cookieToken)) {
      this.persistToken(cookieToken);
      return cookieToken;
    }

    return null;
  }

  clearDeviceInfo(): void {
    this.deviceInfo = null;
    localStateStore.remove(CONFIG.token.storageKey);
    localStateStore.remove('deviceId');
    for (const key of TOKEN_BACKUP_KEYS) {
      localStateStore.remove(key);
    }
    clearTokenCookie();
  }
}

export default DeviceService.getInstance();
